/**
 * Consent API Client
 * 
 * Handles communication with the backend for consent audit logging.
 */

import { ConsentState, CONFIG_VERSION } from './consentConfig';

const API_BASE = '/api/consent';

export interface ConsentLogRequest {
  consentId: string;
  configVersion: string;
  categoriesGranted: string[];
  categoriesDenied: string[];
}

export interface ConsentLogResponse {
  success: boolean;
  consentId: string;
  timestamp: string;
}

/**
 * Log consent decision to backend for audit trail
 * Data minimization: no user identification sent
 */
export async function logConsentToBackend(
  consentId: string,
  state: ConsentState
): Promise<ConsentLogResponse | null> {
  try {
    const categoriesGranted: string[] = ['necessary']; // Always granted
    const categoriesDenied: string[] = [];
    
    if (state.analytics) {
      categoriesGranted.push('analytics');
    } else {
      categoriesDenied.push('analytics');
    }
    
    if (state.marketing) {
      categoriesGranted.push('marketing');
    } else {
      categoriesDenied.push('marketing');
    }
    
    const request: ConsentLogRequest = {
      consentId,
      configVersion: CONFIG_VERSION,
      categoriesGranted,
      categoriesDenied,
    };
    
    const response = await fetch(API_BASE, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });
    
    if (!response.ok) {
      console.warn('Failed to log consent to backend:', response.status);
      return null;
    }
    
    return await response.json();
  } catch (error) {
    // Consent logging failure should not block user experience
    console.warn('Error logging consent to backend:', error);
    return null;
  }
}

/**
 * Get consent record by ID (for user verification)
 */
export async function getConsentRecord(consentId: string): Promise<ConsentLogResponse | null> {
  try {
    const response = await fetch(`${API_BASE}/${consentId}`);
    
    if (!response.ok) {
      return null;
    }
    
    return await response.json();
  } catch (error) {
    console.warn('Error fetching consent record:', error);
    return null;
  }
}

/**
 * Get current consent configuration version from backend
 */
export async function getConfigVersion(): Promise<string | null> {
  try {
    const response = await fetch(`${API_BASE}/config/version`);
    
    if (!response.ok) {
      return null;
    }
    
    const data = await response.json();
    return data.version;
  } catch (error) {
    console.warn('Error fetching config version:', error);
    return null;
  }
}

